import { defineStore } from 'pinia'
import {
  type ComputedRef,
  type Ref,
  isRef,
  watch,
  type WatchStopHandle,
} from 'vue'

type NotificationType = 'error' | 'success' | 'info'

export type MaybeRefOrComputedOrValue<T> = T | ComputedRef<T> | Ref<T>

export type NotificationOptions = {
  type: MaybeRefOrComputedOrValue<Notification['type']>
  title: MaybeRefOrComputedOrValue<Notification['title']>
  message?: MaybeRefOrComputedOrValue<Notification['message']>
  duration?: number
}

export type Notification = {
  id: number
  type: NotificationType
  title: string
  message?: string
  duration: number
}

type State = {
  notifications: Notification[]
  watchers: { id: number; watchStopHandlers: WatchStopHandle[] }[]
  counter: number
}

function unref<T>(maybeRef: MaybeRefOrComputedOrValue<T>) {
  return isRef(maybeRef) ? maybeRef.value : maybeRef
}

const getDefaultState = (): State => ({
  notifications: [],
  watchers: [],
  counter: 0,
})

export const useNotificationStore = defineStore('notification', {
  state: getDefaultState,

  actions: {
    add(options: MaybeRefOrComputedOrValue<NotificationOptions>) {
      const id = this.counter++

      const { duration, type, title, message } = unref(options)

      const notification: Notification = {
        id,
        duration: duration ?? 10000,
        type: unref(type),
        title: unref(title),
        message: unref(message),
      }

      this.notifications.push(notification)

      const findNotification = () => this.notifications.find((n) => n.id === id)

      // Watch for any changes to the refs

      const watchers = {
        id,
        watchStopHandlers: [] as WatchStopHandle[],
      }

      if (isRef(options)) {
        watchers.watchStopHandlers.push(
          watch(
            () => options.value,
            () => {
              const notification = findNotification()

              if (notification) {
                notification.type = unref(options.value.type)
                notification.title = unref(options.value.title)
                notification.message = unref(options.value.message)
              }
            }
          )
        )
      }

      if (isRef(type)) {
        watchers.watchStopHandlers.push(
          watch(
            () => type.value,
            () => {
              const notification = findNotification()

              if (notification) {
                notification.type = type.value
              }
            }
          )
        )
      }

      if (isRef(title)) {
        watchers.watchStopHandlers.push(
          watch(
            () => title.value,
            () => {
              const notification = findNotification()

              if (notification) {
                notification.title = title.value
              }
            }
          )
        )
      }

      if (isRef(message)) {
        watchers.watchStopHandlers.push(
          watch(
            () => message.value,
            () => {
              const notification = findNotification()

              if (notification) {
                notification.message = message.value
              }
            }
          )
        )
      }

      this.watchers.push(watchers)
    },

    remove(notification: Pick<Notification, 'id'>) {
      this.notifications = this.notifications.filter(
        ({ id }) => id !== notification.id
      )

      const watchers = this.watchers.find(({ id }) => id === notification.id)

      watchers?.watchStopHandlers.forEach((unwatch) => unwatch())

      this.watchers = this.watchers.filter(({ id }) => id !== notification.id)
    },
  },
})
